/**
 * Toast contracts shared by the facade, the notification store and the host UI.
 *
 * This file intentionally imports nothing: the notification store references
 * `ToastAction` so toasts can be persisted alongside scoped banners, and the
 * notification layer must stay free of back-references to the toast feature to
 * keep the dependency graph acyclic (toast → notification, never the reverse).
 */

/**
 * Discriminator for a toast's single call-to-action. Keeping link and callback
 * mutually exclusive means one slot, one layout and one focus order — there is
 * no ambiguity when a caller would otherwise supply both a link and a handler.
 */
export enum Kind {
  Link = 'link',
  Callback = 'callback',
}

/** Navigates via an `<a href>`; `external` opens a new, isolated tab. */
export interface ToastLinkAction {
  kind: Kind.Link;
  label: string;
  href: string;
  external?: boolean;
}

/** Runs `handler(id)` from a `<button>`; dismisses the toast unless opted out. */
export interface ToastCallbackAction {
  kind: Kind.Callback;
  label: string;
  handler: (id: string) => void;
  closeOnClick?: boolean;
}

/** The single optional CTA rendered below a toast body. */
export type ToastAction = ToastLinkAction | ToastCallbackAction;

/** Per-toast overrides accepted by every `ToastService` level method. */
export interface ToastOptions {
  /** Optional bold header rendered above the body. */
  title?: string;
  /** Auto-dismiss delay in ms; falls back to the service default. */
  duration?: number;
  /** Whether the close button is shown. Defaults to `true`. */
  closable?: boolean;
  /** Single call-to-action rendered below the body. */
  action?: ToastAction;
}
